import { delay, map } from 'rxjs/operators';
import {
  Component,
  OnInit,
  Input,
  AfterViewInit,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { Order } from '../../../../shared/objects/order/order';
import { TimeslotType } from '../../../../shared/objects/enum/timeslotType';
import { Area } from '../../../../shared/objects/order/area';
import { OrderService } from '../shared/order.service';
import { AvailableTimeslot } from '../../../../shared/objects/order/availableTimeslot';
import { Moment } from 'moment';
import { SelectedTimeslot } from '../../../../shared/objects/order/selectedTimeslot';
import { CommonUtils } from '../../../../shared/utils/common.utils';
import { TimeslotStatus } from '../../../../shared/objects/enum/timeslotStatus';
import { NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { OrderStatus } from '../../../../shared/objects/enum/orderStatus';
import moment from 'moment';
import { ModalService } from '../../../../core/services/modal.service';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { LoadingIndicatorService } from '../../../../core/services/loading-indicator.service';
import { OrderNotificationService } from '../shared/order-notification.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, Subscription } from 'rxjs';
import { MatSelect as MatSelect } from '@angular/material/select';
import { AuthService } from '../../../../core/services/auth.service';

@Component({
  selector: 'app-timeslot-booking',
  templateUrl: './timeslot-booking.component.html',
  styleUrls: ['./timeslot-booking.component.css'],
  standalone: false,
})
export class TimeslotBookingComponent implements OnInit, OnDestroy {
  @Input() order: Order;
  @Input() timeslotType: TimeslotType;
  @Input() modalRefItself: NgbModalRef;
  @Input() editMode: boolean;

  @ViewChild('areaSelect') areaSelect: MatSelect;

  areas: Area[];
  selectedAreaId: any;
  selectedDate: Moment;
  selectedDock: number;
  availableTimeslots: AvailableTimeslot[] = [];
  selectedTimeslotId: number;
  timeslotBookingIsAvailable = true;
  selectedArea: Area;
  childPopUpShown: boolean;
  validPeriodHasPassed = false;
  loadedAreas: boolean;

  private acceptOrderSuccessMessage: string;
  private acceptOrderErrorMessage: string;
  private timeslotsFetchComplete = false;
  private subscriptions: Subscription[] = [];

  get isImpersonated() {
    return this.authService.isImpersonated();
  }
  get areaHasMultipleDocksAvailable() {
    return (
      !!this.availableTimeslots &&
      this.availableTimeslots.some(area => area.dockNumber > 1)
    );
  }

  get TimeslotType() {
    return TimeslotType;
  }

  get CommonUtils(): CommonUtils {
    return CommonUtils;
  }

  get isLoading(): boolean {
    return this.timeslotType === TimeslotType.Loading;
  }

  get isDelivery(): boolean {
    return this.timeslotType === TimeslotType.Delivery;
  }

  get orderStatus(): string {
    return this.order.status;
  }

  get orderStatusIsAnnouncedToCarrier(): boolean {
    return this.orderStatus === OrderStatus.AnnouncedToCarrier.toString();
  }

  get orderStatusIsTimeExpired(): boolean {
    return this.orderStatus === OrderStatus.TimeExpired.toString();
  }

  get datesAvailableForBooking(): boolean {
    if (this.selectedAreaId === undefined) {
      return true;
    }
    return this.isLoading
      ? !!this.order.loadingDates && this.order.loadingDates.length > 0
      : !!this.order.deliveryDates && this.order.deliveryDates.length > 0;
  }

  get timeslotsAvailableForBooking(): boolean {
    if (
      this.selectedAreaId === undefined ||
      !this.selectedDate ||
      !this.timeslotsFetchComplete
    ) {
      return true;
    }
    return !!this.availableTimeslots && this.availableTimeslots.length > 0;
  }

  get canSelectTimeslots(): boolean {
    return this.isLoading
      ? !!this.order &&
          moment(this.order.loadingDate).isSame(
            moment(this.order.maxLoadingDateTime),
          )
      : !!this.order &&
          moment(this.order.delivDate).isSame(
            moment(this.order.maxDeliveryDateTime),
          );
  }

  get timeslotDisabled(): boolean {
    return (
      !this.timeslotBookingIsAvailable ||
      !this.datesAvailableForBooking ||
      !this.timeslotsAvailableForBooking ||
      this.validPeriodHasPassed ||
      !this.selectedDate
    );
  }
  newAvailableTimeslot: AvailableTimeslot;
  noTimeslotsForCurrentDate = false;
  constructor(
    private orderService: OrderService,
    public activeModal: NgbActiveModal,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private orderNotificationService: OrderNotificationService,
    private spinner: NgxSpinnerService,
    private authService: AuthService,
  ) {
    this.setupNotificationMessages();
  }
  public dateDirtyOnInit = false;
  private focusOnInit: Subject<boolean> = new Subject<boolean>();
  private focusOnInit$ = this.focusOnInit.asObservable();

  ngOnInit() {
    this.subscriptions.push(
      this.focusOnInit$.pipe(delay(0)).subscribe(() => {
        this.areaSelect.focus();
      }),
    );
    this.spinner.show();
    this.dateDirtyOnInit = true;
    if (this.timeslotType === TimeslotType.Loading) {
      if (this.order.loadingTimeslot) {
        this.selectedAreaId = this.order.loadingTimeslot.areaId;
        this.selectedDate = this.order.loadingTimeslot.date;
        this.newAvailableTimeslot = {
          id: 0,
          dockNumber: +this.order.loadingTimeslot.dock,
          end: null,
          start: moment(this.order.loadingTimeslot.date)
            .add(moment.duration(this.order.loadingTimeslot.start))
            .format('YYYY-MM-DDTHH:mm'),
        };
        this.selectedTimeslotId = this.newAvailableTimeslot.id;
        this.availableTimeslots.push(this.newAvailableTimeslot);
      }
      this.getAreas(this.order.orderId, this.order.loadingAddressId);
    } else if (this.timeslotType === TimeslotType.Delivery) {
      if (this.order.deliveryTimeslot) {
        this.selectedAreaId = this.order.deliveryTimeslot.areaId;
        this.selectedDate = this.order.deliveryTimeslot.date;

        this.newAvailableTimeslot = {
          id: 0,
          dockNumber: +this.order.deliveryTimeslot.dock,
          end: null,
          start: moment(this.order.deliveryTimeslot.date)
            .add(moment.duration(this.order.deliveryTimeslot.start))
            .format('YYYY-MM-DDTHH:mm'),
        };
        this.selectedTimeslotId = this.newAvailableTimeslot.id;
        this.availableTimeslots.push(this.newAvailableTimeslot);
      }

      this.getAreas(this.order.orderId, this.order.deliveryAddressId);
    }

    this.filterTimeslotDates();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private focusOnArea() {
    this.focusOnInit.next(true);
  }
  public resetForm() {
    this.ngOnInit();
  }

  private getAreas(orderId: string, addressId: number) {
    this.loadedAreas = false;
    this.orderService.getAreas(orderId, addressId).subscribe(areas => {
      this.areas = areas;
      this.loadedAreas = true;
      this.focusOnArea();
      this.onTimeslotDateChange();
    });
  }

  getAvailableTimeslots() {
    const areaID = this.selectedAreaId;
    this.selectedArea = this.areas.find(a => a.areaId === areaID);
    this.timeslotBookingIsAvailable = this.isBookingAvailable();
    if (this.selectedAreaId && this.selectedDate) {
      this.spinner.show();
      this.orderService
        .getAvailableTimeslots(
          areaID,
          this.selectedDate,
          Number(this.order.orderId),
        )
        .subscribe(
          timeslots => {
            this.availableTimeslots = [];
            if (this.timeslotType === TimeslotType.Loading) {
              this.availableTimeslots = timeslots.filter(
                timeslot =>
                  this.canSelectTimeslots ||
                  moment(timeslot.end).isBefore(
                    moment(this.order.maxLoadingDateTime),
                  ),
              );
            } else {
              this.availableTimeslots = timeslots.filter(
                timeslot =>
                  this.canSelectTimeslots ||
                  moment(timeslot.end).isBefore(
                    moment(this.order.maxDeliveryDateTime),
                  ),
              );
            }
            this.availableTimeslots = this.availableTimeslots.map(
              (timeslot, index) => {
                const newTimeslot: AvailableTimeslot = {
                  dockNumber: timeslot.dockNumber,
                  end: timeslot.end,
                  start: timeslot.start,
                  id: index,
                };
                return newTimeslot;
              },
            );
            this.timeslotsFetchComplete = true;
            this.validPeriodHasPassed = false;

            // to have all the available timeslots on the client
            if (
              this.timeslotType === TimeslotType.Loading &&
              this.order.loadingTimeslot
            ) {
              this.newAvailableTimeslot = {
                id: 0,
                dockNumber: +this.order.loadingTimeslot.dock,
                end: null,
                start: moment(this.order.loadingTimeslot.date)
                  .add(moment.duration(this.order.loadingTimeslot.start))
                  .format('YYYY-MM-DDTHH:mm:ss'),
              };
            } else if (
              this.timeslotType === TimeslotType.Delivery &&
              this.order.deliveryTimeslot
            ) {
              this.newAvailableTimeslot = {
                id: 0,
                dockNumber: +this.order.deliveryTimeslot.dock,
                end: null,
                start: moment(this.order.deliveryTimeslot.date)
                  .add(moment.duration(this.order.deliveryTimeslot.start))
                  .format('YYYY-MM-DDTHH:mm:ss'),
              };
            }

            this.selectedTimeslotId = null;
            if (this.selectedAreaId) {
              const timeslotValid = this.timeslotIsValid(this.timeslotType);

              if (timeslotValid) {
                const foundTimeslot = this.availableTimeslots.find(
                  availableTimeslot =>
                    +availableTimeslot.dockNumber ===
                      this.newAvailableTimeslot.dockNumber &&
                    availableTimeslot.start === this.newAvailableTimeslot.start,
                );
                this.selectedTimeslotId = foundTimeslot
                  ? foundTimeslot.id
                  : null;
              }
            }
            this.spinner.hide();
          },
          error => {
            this.availableTimeslots = [];
            this.timeslotsFetchComplete = true;
            this.spinner.hide();
          },
        );
    }
  }

  setTimeslot() {
    this.spinner.show();
    if (
      this.selectedAreaId &&
      this.selectedDate &&
      this.selectedTimeslotId != null
    ) {
      let currentType = null;
      let areaID: number;
      areaID = this.selectedAreaId;

      const ts = new SelectedTimeslot();
      ts.areaId = areaID;
      const selectedTimeslot = this.availableTimeslots.find(
        timeslot => timeslot.id === this.selectedTimeslotId,
      );
      const dockNr = Number(selectedTimeslot.dockNumber);
      ts.dock = !!dockNr ? dockNr : 1;
      ts.date = this.selectedDate;
      ts.start = moment(selectedTimeslot.start).format('HH:mm');

      if (this.timeslotType === TimeslotType.Loading) {
        currentType = TimeslotType.Loading;
        this.order.loadingTimeslot = ts;
      } else if (this.timeslotType === TimeslotType.Delivery) {
        this.order.deliveryTimeslot = ts;
        currentType = TimeslotType.Delivery;
      }

      this.saveTimeslotOrder(currentType)
        .then(() => {
          if (
            this.orderStatusIsAnnouncedToCarrier ||
            this.orderStatusIsTimeExpired
          ) {
            this.acceptOrder();
            this.validPeriodHasPassed = false;
          } else {
            this.activeModal.dismiss('close');
          }
        })
        .catch(err => {
          if (err.error === 'TIMESLOT-CONFIRMED') {
            this.timeslotBookingIsAvailable = false;
          } else {
            this.validPeriodHasPassed = true;
          }
        })
        .finally(() => {
          this.spinner.hide();
        });
    }
  }

  private saveTimeslotOrder(timeslotType: TimeslotType) {
    return this.orderService
      .saveTimeslot(this.order, timeslotType)
      .pipe(
        map(
          val => {
            this.translateService
              .get('TIMESLOT.TIMESLOT-BOOKED-SUCCESSFULLY')
              .toPromise()
              .then(successMessage => this.toastrService.info(successMessage));
          },
          error => {
            this.translateService
              .get('TIMESLOT.TIMESLOT-BOOKED-FAILURE')
              .toPromise()
              .then(errorMessage => this.toastrService.error(errorMessage));
          },
        ),
      )
      .toPromise();
  }

  updateChangedOrder(): void {
    this.refreshOrderInfo();
  }

  private refreshOrderInfo() {
    this.orderService.getOrder(this.order.id).subscribe(orderInfo => {
      this.order = Order.fromObj(orderInfo);
    });
  }

  private acceptOrder() {
    this.orderService
      .acceptOrder(+this.order.id)
      .then(
        val => {
          this.toastrService.info(this.acceptOrderSuccessMessage);
          this.updateChangedOrder();
          this.validPeriodHasPassed = false;
          this.activeModal.dismiss('close');
        },
        error => {
          this.toastrService.error(this.acceptOrderErrorMessage);
        },
      )
      .catch(() => {
        this.validPeriodHasPassed = true;
      })
      .finally(() => {
        this.spinner.hide();
      });
  }

  private timeslotCanBeChanged(
    order: Order,
    timeslotType: TimeslotType,
  ): boolean {
    const timeslot = this.getRightOrderTimeslot(order, timeslotType);
    return (
      !timeslot || (timeslot && timeslot.status !== TimeslotStatus.Confirmed)
    );
  }

  private isBookingAvailable(): boolean {
    return this.timeslotCanBeChanged(this.order, this.timeslotType);
  }

  private getRightOrderTimeslot(
    order: Order,
    timeslotType: TimeslotType,
  ): SelectedTimeslot {
    return (
      order &&
      (timeslotType === TimeslotType.Loading
        ? order.loadingTimeslot
        : timeslotType === TimeslotType.Delivery
          ? order.deliveryTimeslot
          : null)
    );
  }

  private filterTimeslotDates() {
    if (this.isLoading) {
      if (
        moment(this.order.loadingDate).isSame(
          moment(this.order.maxLoadingDateTime),
        )
      ) {
        return;
      }
      if (moment(this.order.maxLoadingDateTime) < moment(new Date())) {
        this.order.loadingDates = [];
      }
    } else {
      if (
        moment(this.order.delivDate).isSame(
          moment(this.order.maxDeliveryDateTime),
        )
      ) {
        return;
      }
      if (moment(this.order.maxDeliveryDateTime) < moment(new Date())) {
        this.order.deliveryDates = [];
      }
    }
  }

  onAreaChange(): void {
    this.selectedDate = null;
    this.selectedTimeslotId = null;
    this.onTimeslotDateChange();
  }

  setDirtyDateFalse() {
    this.dateDirtyOnInit = false;
  }

  onTimeslotDateChange() {
    if (!this.dateDirtyOnInit) {
      this.selectedTimeslotId = null;
    }

    this.timeslotsFetchComplete = false;

    if (!!this.selectedAreaId && !!this.selectedDate) {
      this.spinner.show();
      this.orderService
        .getBookingPeriodExpired(
          parseInt(this.order.id),
          this.timeslotType,
          this.selectedAreaId,
          this.selectedDate,
        )
        .subscribe(val => {
          this.validPeriodHasPassed = val;
          this.timeslotBookingIsAvailable = this.isBookingAvailable();
          if (!this.validPeriodHasPassed) {
            this.getAvailableTimeslots();
          } else {
            this.spinner.hide();
            this.timeslotsFetchComplete = true;
          }
        });
    } else {
      this.spinner.hide();
      this.timeslotsFetchComplete = true;
    }
  }

  private timeslotIsValid(timeslotType: TimeslotType): boolean {
    switch (timeslotType) {
      case TimeslotType.Loading:
        if (!this.order.loadingTimeslot) {
          return false;
        }
        return (
          this.order.loadingTimeslot.areaId === this.selectedAreaId &&
          this.order.loadingTimeslot.date === this.selectedDate
        );
      case TimeslotType.Delivery:
        if (!this.order.deliveryTimeslot) {
          return false;
        }
        return (
          this.order.deliveryTimeslot.areaId === this.selectedAreaId &&
          this.order.deliveryTimeslot.date === this.selectedDate
        );
      default:
        return false;
    }
  }

  fadeToggle(fadeOn: boolean): void {
    this.childPopUpShown = fadeOn;
  }

  private setupNotificationMessages() {
    this.translateService
      .get('TIMESLOT.ORDER-WAS-ACCEPTED')
      .subscribe(t => (this.acceptOrderSuccessMessage = t));
    this.translateService
      .get('TIMESLOT.ERROR-AT-ACCEPTING-ORDER')
      .subscribe(t => (this.acceptOrderErrorMessage = t));
  }
}
